import { useActiveTenantEditable } from './useActiveTenantEditable';
import { useTenantAccess } from '../auth/useTenantAccess';

/**
 * Phase 3 — per-affordance read-only helper (belt; ReadOnlyGuard is the
 * suspenders). Spread `disabledProps` onto a write button/input to disable it
 * with an explanatory tooltip when the selected env is read-only.
 *
 * WAVE G — now consults useActiveTenantEditable so the gate also accounts for
 * per-tenant authoring homes when a tenant is in context. With no tenant (or
 * the per-tenant backend off) it collapses to pure env-level editability —
 * byte-identical to today. When a tenant IS homed on another env, the tooltip
 * names the home so the user knows where to switch.
 */
export function useWriteGuard(): {
  readOnly: boolean;
  /** Context-appropriate heading for the UploadZone read-only state. */
  uploadHeading: string;
  disabledProps: { disabled?: boolean; title?: string };
} {
  const { editable, tenantCode, editableHere, homeEnv } = useActiveTenantEditable();
  // Per-tenant access (mig 246): if the user only has read access to the active
  // tenant, everything is read-only regardless of env editability. No-op while
  // ACCESS_ENFORCEMENT is off (accessReadOnly stays false).
  const { readOnly: accessReadOnly } = useTenantAccess(tenantCode || undefined);

  if (accessReadOnly) {
    return {
      readOnly: true,
      uploadHeading: 'You don\'t have write access to this tenant',
      disabledProps: { disabled: true, title: 'Read-only: you have read-only access to this tenant.' },
    };
  }

  if (editable) {
    return { readOnly: false, uploadHeading: '', disabledProps: {} };
  }

  // Distinguish "this env is read-only" from "this tenant is authored elsewhere"
  // so the tooltip is actionable. editableHere=false with a tenant in context
  // means the tenant's home is a different env.
  const title =
    tenantCode && !editableHere
      ? `Read-only: this tenant is authored on "${homeEnv}". Switch to "${homeEnv}" to make changes.`
      : 'Read-only: switch to the editable environment to make changes.';

  return {
    readOnly: true,
    uploadHeading: 'Uploading is only available in the development environment',
    disabledProps: { disabled: true, title },
  };
}

export default useWriteGuard;
